import React, { useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-secondary/80 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal Content */}
      <div className="relative bg-background rounded-2xl border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden brand-shadow-lg">
        {/* Modal Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="FolderOpen" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-brand-headline text-foreground">
                {project?.title}
              </h2>
              <p className="text-text-secondary font-brand-body">
                {project?.category} • {project?.year}{project?.role && ` • ${project.role}`}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={24}
            className="text-text-secondary hover:text-primary"
            aria-label="Close modal"
          />
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto max-h-[calc(90vh-100px)]">
          <div className="p-6 space-y-8">
            {/* Project Image */}
            <div className="relative h-64 md:h-80 rounded-xl overflow-hidden">
              <Image
                src={project?.image}
                alt={project?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className={`px-3 py-1 rounded-full text-sm font-brand-medium ${
                  project?.status === 'Live' ?'bg-success text-success-foreground' 
                    : project?.status === 'In Development' ?'bg-warning text-warning-foreground' :'bg-accent text-accent-foreground'
                }`}>
                  {project?.status}
                </span>
              </div>
            </div>

            {/* Project Overview */}
            <div>
              <h3 className="text-xl font-brand-subheading text-foreground mb-4">
                Project Overview
              </h3>
              <p className="text-text-primary font-brand-body leading-relaxed">
                {project?.fullDescription}
              </p>
            </div>

            {/* Technology Stack */}
            <div>
              <h3 className="text-xl font-brand-subheading text-foreground mb-4">
                Technology Stack
              </h3>
              <div className="flex flex-wrap gap-3">
                {project?.technologies?.map((tech, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-primary/10 text-primary font-brand-medium rounded-lg border border-primary/20"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Key Metrics */}
            <div>
              <h3 className="text-xl font-brand-subheading text-foreground mb-4">
                Key Achievements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {project?.metrics?.map((metric, index) => (
                  <div key={index} className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-brand-headline text-primary mb-2">
                      {metric?.value}
                    </div>
                    <div className="text-text-secondary font-brand-body">
                      {metric?.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Technical Approach */}
            <div>
              <h3 className="text-xl font-brand-subheading text-foreground mb-4">
                Technical Approach
              </h3>
              <div className="space-y-4">
                {project?.technicalApproach?.map((approach, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-brand-medium text-sm">
                        {index + 1}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-brand-medium text-foreground mb-1">
                        {approach?.title}
                      </h4>
                      <p className="text-text-secondary font-brand-body text-sm">
                        {approach?.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Lessons Learned */}
            <div>
              <h3 className="text-xl font-brand-subheading text-foreground mb-4">
                Lessons Learned
              </h3>
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="Lightbulb" size={24} className="text-accent flex-shrink-0 mt-1" />
                  <p className="text-text-primary font-brand-body leading-relaxed">
                    {project?.lessonsLearned}
                  </p>
                </div>
              </div>
            </div>

            {/* Client Testimonial */}
            {project?.testimonial && (
              <div>
                <h3 className="text-xl font-brand-subheading text-foreground mb-4">
                  Client Feedback
                </h3>
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <Image
                      src={project?.testimonial?.avatar}
                      alt={project?.testimonial?.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <blockquote className="text-text-primary font-brand-body italic mb-3">
                        "{project?.testimonial?.content}"
                      </blockquote>
                      <div>
                        <div className="font-brand-medium text-foreground">
                          {project?.testimonial?.name}
                        </div>
                        <div className="text-sm text-text-secondary">
                          {project?.testimonial?.position}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-border">
              {project?.liveUrl && (
                <Button
                  variant="default"
                  asChild
                  iconName="ExternalLink"
                  iconPosition="left"
                  iconSize={18}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <a href={project?.liveUrl} target="_blank" rel="noopener noreferrer">
                    View Live Project
                  </a>
                </Button>
              )}
              
              {project?.githubUrl && (
                <Button
                  variant="outline"
                  asChild
                  iconName="Github"
                  iconPosition="left"
                  iconSize={18}
                  className="border-primary/20 text-primary hover:bg-primary/10"
                >
                  <a href={project?.githubUrl} target="_blank" rel="noopener noreferrer">
                    View Source Code
                  </a>
                </Button>
              )}
              
              <Button
                variant="secondary"
                onClick={onClose}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={18}
                className="sm:ml-auto"
              >
                Back to Projects
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;